import { CircleNotch, DotsThree, Sparkle, Warning } from '@phosphor-icons/react';
import { useGetBrandProfile } from '@reelplicate/api-client/brand-profiles';
import type { FlowItemDto, NodeRunDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import type { FlowNode as GraphNode, Issue, NodeDefinition } from '@reelplicate/flows';
import {
  Badge,
  CATEGORY_TINT,
  cn,
  FLOW_TINTS,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@reelplicate/ui';
import { Handle, type Node, type NodeProps, Position } from '@xyflow/react';
import { memo } from 'react';

import { CATEGORY_NAME, NODE_ICONS } from '../catalogue-ui';
import { HeldItems } from './run/held-items';

export type FlowNodeData = {
  node: GraphNode;
  definition: NodeDefinition;
  error?: Issue;
  /** Something to know before running (it doesn't block). */
  warning?: Issue;
  /** Handle ids (`in:hook`, `out:out`) with an edge attached. */
  connected: ReadonlySet<string>;
  onDelete: (nodeId: string) => void;
  /** This node's progress in the run the canvas shows. */
  run?: NodeRunDto;
  /** Its items waiting for a fix. */
  held: FlowItemDto[];
};
export type FlowCanvasNode = Node<FlowNodeData, 'flow'>;

/** Header centre line, where single ports sit (Figma: ports centred on the header). */
const HEADER_PORT_Y = 29;
/** Multi-port nodes list their ports in rows under the header. */
const PORT_ROWS_TOP = 52;
const PORT_ROW = 24;

/**
 * A workflow step on the canvas (Figma Node, 68:193): 248 wide, radius/lg,
 * 1px border/default + Shadow/XS; tinted 28px icon tile, Label/Medium title,
 * Caption kind, 24px menu; status footer. Selected: 2px brand/strong +
 * Shadow/MD. Error: 2px status/danger/fg + the message (never colour alone).
 */
/** "Rendering 2 of 4…" on the node while its tasks run. */
const BUSY_VERBS: Record<NodeDefinition['executor'], string> = {
  control: 'Working',
  llm: 'Generating',
  render: 'Rendering',
  publish: 'Publishing',
};

export const FlowNode = memo(function FlowNode({
  data,
  selected,
}: NodeProps<FlowCanvasNode>) {
  const { node, definition, error, warning, connected, onDelete, run, held } = data;
  const tint = FLOW_TINTS[CATEGORY_TINT[definition.category]];
  const Icon = NODE_ICONS[definition.icon] ?? Sparkle;
  const title = node.label ?? definition.label;
  const inputs = definition.inputs;
  const outputs = definition.outputs;
  const rows = Math.max(inputs.length, outputs.length) > 1;
  const portY = (i: number) => (rows ? PORT_ROWS_TOP + i * PORT_ROW + 10 : HEADER_PORT_Y);

  return (
    <div
      data-slot="flow-node"
      data-node-id={node.id}
      data-selected={selected || undefined}
      data-error={error ? true : undefined}
      data-warning={!error && warning ? true : undefined}
      className={cn(
        'bg-bg-surface border-border-default group/node relative flex w-62 flex-col rounded-lg border shadow-xs',
        'transition-[border-color,box-shadow] duration-[120ms] ease-out hover:border-border-strong hover:shadow-md',
        selected &&
          'border-brand-strong inset-ring-brand-strong shadow-md inset-ring-1 hover:border-brand-strong',
        error &&
          !selected &&
          'border-status-danger-fg inset-ring-status-danger-fg inset-ring-1 hover:border-status-danger-fg',
        !error &&
          warning &&
          !run &&
          !selected &&
          'border-status-warning-fg hover:border-status-warning-fg',
      )}
    >
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <span
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-sm',
            tint.soft,
          )}
        >
          <Icon aria-hidden className={cn('size-4', tint.text)} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-label-md text-text-primary truncate">{title}</span>
          <span className="text-caption text-text-tertiary truncate">
            {CATEGORY_NAME[definition.category]}
            {node.label ? ` · ${definition.label}` : ''}
          </span>
        </div>
        <Menu>
          <MenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions for ${title}`}
              className="nodrag text-icon-secondary hover:bg-bg-muted focus-visible:shadow-focus-ring flex size-6 shrink-0 items-center justify-center rounded-md outline-none"
            >
              <DotsThree className="size-4" />
            </button>
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem type="danger" onSelect={() => onDelete(node.id)}>
              Delete node
            </MenuItem>
          </MenuContent>
        </Menu>
      </div>

      {rows && (
        <div className="text-caption text-text-tertiary flex flex-col gap-1 px-3 pb-2">
          {Array.from({ length: Math.max(inputs.length, outputs.length) }, (_, i) => (
            <div key={i} className="flex h-5 items-center justify-between">
              <span>{inputs[i]?.label ?? ''}</span>
              <span>{outputs[i]?.label ?? ''}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex min-h-8 items-center gap-1.5 px-3 pb-3">
        <NodeStatus
          error={
            error
              ? error.message
                  .replace(`${title}: `, '')
                  .replace(`${definition.label}: `, '')
              : undefined
          }
          warning={warning?.message}
          run={run}
          held={held}
          busyVerb={BUSY_VERBS[definition.executor]}
          guardrail={definition.type === GUARDRAIL}
        />
      </div>
      {run?.status === 'RUNNING' && run.tasksTotal > 0 && (
        <div
          aria-hidden
          className="bg-bg-muted absolute inset-x-0 top-0 h-0.75 overflow-hidden rounded-t-lg"
        >
          <div
            className="bg-brand-primary h-full transition-[width] duration-300 ease-out"
            style={{ width: `${(run.tasksDone / run.tasksTotal) * 100}%` }}
          />
        </div>
      )}

      {inputs.map((port, i) => (
        <Port
          key={`in:${port.id}`}
          type="target"
          id={port.id}
          top={portY(i)}
          trigger={port.accepts === 'trigger'}
          connected={connected.has(`in:${port.id}`)}
          label={`${title}: ${port.label ?? 'input'}`}
        />
      ))}
      {outputs.map((port, i) => (
        <Port
          key={`out:${port.id}`}
          type="source"
          id={port.id}
          top={portY(i)}
          trigger={port.type === 'trigger'}
          connected={connected.has(`out:${port.id}`)}
          label={`${title}: ${port.label ?? 'output'}`}
        />
      ))}
    </div>
  );
});

/**
 * Figma Port (68:9): 16px hit area around a 12px dot. Default bg/surface with a
 * 1.5px border/strong ring; hover grows and takes border/focus; connected
 * fills brand/strong with a surface ring. Trigger handles are dashed.
 */
function Port({
  type,
  id,
  top,
  connected,
  trigger,
  label,
}: {
  type: 'source' | 'target';
  id: string;
  top: number;
  connected: boolean;
  trigger: boolean;
  label: string;
}) {
  return (
    <Handle
      type={type}
      id={id}
      position={type === 'target' ? Position.Left : Position.Right}
      aria-label={label}
      style={{ top }}
      className={cn(
        '!size-3 !rounded-full !border-[1.5px] !border-border-strong !bg-bg-surface',
        'transition-[scale,border-color,background-color] duration-[120ms] ease-out hover:!scale-125 hover:!border-border-focus',
        'after:absolute after:-inset-1 after:content-[""]',
        connected && '!border-bg-surface !bg-brand-strong ring-brand-strong ring-1',
        trigger && '!border-dashed',
        '[&.connectingto.valid]:!border-border-focus [&.connectingto.valid]:!scale-125',
      )}
    />
  );
}

/**
 * The node's footer. A draft error wins; then the run's state (Figma Running:
 * spinner + "Generating 9 of 15…"; Done · N; Waiting); else Ready.
 */
function NodeStatus({
  error,
  warning,
  run,
  held,
  busyVerb,
  guardrail = false,
}: {
  error?: string;
  /** Shown at rest, before a run: it doesn't block. */
  warning?: string;
  run?: NodeRunDto;
  held: FlowItemDto[];
  busyVerb: string;
  /** Brand guardrails: says when there's nothing to check against. */
  guardrail?: boolean;
}) {
  const message =
    error ?? (run?.status === 'FAILED' ? (run.lastError ?? 'Failed') : undefined);
  if (message) {
    return (
      <>
        <Warning aria-hidden className="text-status-danger-fg size-3.5 shrink-0" />
        <span className="text-caption text-status-danger-fg line-clamp-2">{message}</span>
      </>
    );
  }
  if (!run && warning) {
    return (
      <>
        <Warning aria-hidden className="text-status-warning-fg size-3.5 shrink-0" />
        <span className="text-caption text-status-warning-fg line-clamp-3">
          {warning}
        </span>
      </>
    );
  }
  if (!run) {
    return guardrail ? (
      <GuardrailReady />
    ) : (
      <Badge tone="success" dot>
        Ready
      </Badge>
    );
  }
  const heldChip = held.length > 0 && <HeldItems items={held} />;
  switch (run.status) {
    case 'RUNNING':
      return (
        <>
          <CircleNotch
            aria-hidden
            className="text-status-info-fg size-3.5 shrink-0 animate-spin motion-reduce:animate-none"
          />
          <span className="text-caption text-status-info-fg">
            {busyVerb} {run.tasksDone} of {run.tasksTotal}…
          </span>
          {heldChip}
        </>
      );
    case 'WAITING':
      return (
        <>
          <Badge tone="info" dot>
            Waiting on review · {run.tasksTotal - run.tasksDone}
          </Badge>
          {heldChip}
        </>
      );
    case 'SLEEPING':
      return (
        <>
          <Badge tone="info" dot>
            Measuring · {run.tasksTotal - run.tasksDone}
          </Badge>
          {heldChip}
        </>
      );
    case 'DONE':
      return (
        <>
          <Badge tone="success" dot>
            Done · {run.itemsOut}
          </Badge>
          {heldChip}
        </>
      );
    case 'SKIPPED':
      return <Badge dot>Skipped</Badge>;
    default:
      return <Badge dot>Waiting</Badge>;
  }
}

const GUARDRAIL = 'guardrail.brandGuardrails';

/** Brand guardrails at rest: Ready, or a warning while the brand profile has no rules to check. */
function GuardrailReady() {
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const { data: profile } = useGetBrandProfile(workspaceId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false, retry: false },
  });
  if (profile && profile.approvedClaims.length + profile.prohibited.length === 0) {
    return (
      <Badge tone="warning" dot>
        No guardrails · skipped
      </Badge>
    );
  }
  return (
    <Badge tone="success" dot>
      Ready
    </Badge>
  );
}
